import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Location } from '../types';
import { locationsApi } from '../services/api';

const CURRENT_LOCATION_KEY = 'keykeeper_current_location';

interface LocationsState {
  locations: Location[];
  currentLocationId: string | null;
  loading: boolean;
  error: string | null;
}

// Load current location from localStorage
const loadCurrentLocationId = (): string | null => {
  try {
    return localStorage.getItem(CURRENT_LOCATION_KEY);
  } catch {
    return null;
  }
};

const initialState: LocationsState = {
  locations: [],
  currentLocationId: loadCurrentLocationId(),
  loading: false,
  error: null,
};

// Async thunks for API operations
export const fetchLocations = createAsyncThunk(
  'locations/fetchLocations',
  async (_, { rejectWithValue }) => {
    try {
      return await locationsApi.getAll();
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const createLocationAsync = createAsyncThunk(
  'locations/createLocation',
  async (location: Location, { rejectWithValue }) => {
    try {
      return await locationsApi.create(location);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const updateLocationAsync = createAsyncThunk(
  'locations/updateLocation',
  async (location: Location, { rejectWithValue }) => {
    try {
      return await locationsApi.update(location);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const deleteLocationAsync = createAsyncThunk(
  'locations/deleteLocation',
  async (id: string, { rejectWithValue }) => {
    try {
      await locationsApi.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

const locationsSlice = createSlice({
  name: 'locations',
  initialState,
  reducers: {
    setLocations: (state, action: PayloadAction<Location[]>) => {
      state.locations = action.payload;
      // Auto-select first location if none selected and locations exist
      if (!state.currentLocationId && action.payload.length > 0) {
        state.currentLocationId = action.payload[0].id;
        try {
          localStorage.setItem(CURRENT_LOCATION_KEY, action.payload[0].id);
        } catch {
          // Ignore localStorage errors
        }
      }
      // If current location is no longer valid, select first location
      if (state.currentLocationId && action.payload.length > 0) {
        const locationExists = action.payload.some(loc => loc.id === state.currentLocationId);
        if (!locationExists) {
          state.currentLocationId = action.payload[0].id;
          try {
            localStorage.setItem(CURRENT_LOCATION_KEY, action.payload[0].id);
          } catch {
            // Ignore localStorage errors
          }
        }
      }
    },
    setCurrentLocation: (state, action: PayloadAction<string>) => {
      state.currentLocationId = action.payload;
      try {
        localStorage.setItem(CURRENT_LOCATION_KEY, action.payload);
      } catch {
        // Ignore localStorage errors
      }
    },
    addLocation: (state, action: PayloadAction<Location>) => {
      state.locations.push(action.payload);
      // Auto-select if this is the first location
      if (!state.currentLocationId) {
        state.currentLocationId = action.payload.id;
        try {
          localStorage.setItem(CURRENT_LOCATION_KEY, action.payload.id);
        } catch {
          // Ignore localStorage errors
        }
      }
    },
    updateLocation: (state, action: PayloadAction<Location>) => {
      const index = state.locations.findIndex(loc => loc.id === action.payload.id);
      if (index !== -1) {
        state.locations[index] = action.payload;
      }
    },
    removeLocation: (state, action: PayloadAction<string>) => {
      state.locations = state.locations.filter(loc => loc.id !== action.payload);
      // If removing current location, select first remaining location
      if (state.currentLocationId === action.payload && state.locations.length > 0) {
        state.currentLocationId = state.locations[0].id;
        try {
          localStorage.setItem(CURRENT_LOCATION_KEY, state.locations[0].id);
        } catch {
          // Ignore localStorage errors
        }
      }
    },
    clearLocationError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch locations
      .addCase(fetchLocations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLocations.fulfilled, (state, action) => {
        state.loading = false;
        state.locations = action.payload;
        // Auto-select first location if none selected
        if (!state.currentLocationId && action.payload.length > 0) {
          state.currentLocationId = action.payload[0].id;
          try {
            localStorage.setItem(CURRENT_LOCATION_KEY, action.payload[0].id);
          } catch {
            // Ignore localStorage errors
          }
        }
        // Validate current location still exists
        if (state.currentLocationId && action.payload.length > 0) {
          const locationExists = action.payload.some(loc => loc.id === state.currentLocationId);
          if (!locationExists) {
            state.currentLocationId = action.payload[0].id;
            try {
              localStorage.setItem(CURRENT_LOCATION_KEY, action.payload[0].id);
            } catch {
              // Ignore localStorage errors
            }
          }
        }
      })
      .addCase(fetchLocations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create location
      .addCase(createLocationAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createLocationAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.locations.push(action.payload);
      })
      .addCase(createLocationAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update location
      .addCase(updateLocationAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateLocationAsync.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.locations.findIndex(loc => loc.id === action.payload.id);
        if (index !== -1) {
          state.locations[index] = action.payload;
        }
      })
      .addCase(updateLocationAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete location
      .addCase(deleteLocationAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteLocationAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.locations = state.locations.filter(loc => loc.id !== action.payload);
      })
      .addCase(deleteLocationAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setLocations, setCurrentLocation, addLocation, updateLocation, removeLocation, clearLocationError } = locationsSlice.actions;
export default locationsSlice.reducer;
